"use client";

import styles from "./useScrollToHighlight.module.css";

const ANIMATION_DURATION_MS = 2000;
const MAX_WAIT_MS = 10000;
const POLL_INTERVAL_MS = 200;
const STABLE_THRESHOLD_MS = 150;

let activeInterval: ReturnType<typeof setInterval> | null = null;

function cleanup() {
  if (activeInterval) {
    clearInterval(activeInterval);
    activeInterval = null;
  }
}

function startPolling(highlightId: string) {
  cleanup();

  const startTime = Date.now();
  let lastTop: number | null = null;
  let stableSince: number | null = null;

  activeInterval = setInterval(() => {
    const el = document.getElementById(highlightId);

    if (!el) {
      if (Date.now() - startTime > MAX_WAIT_MS) {
        cleanup();
      }
      return;
    }

    // Once the element exists, wait for its position to stabilize.
    // This ensures content above it (e.g. loading spinners → full forms)
    // has finished rendering before we scroll.
    const currentTop = el.getBoundingClientRect().top;

    if (lastTop !== null && Math.abs(currentTop - lastTop) < 2) {
      // Position is stable
      if (!stableSince) {
        stableSince = Date.now();
      } else if (Date.now() - stableSince >= STABLE_THRESHOLD_MS) {
        cleanup();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add(styles.highlightPulse);
        setTimeout(() => {
          el.classList.remove(styles.highlightPulse);
        }, ANIMATION_DURATION_MS);
        return;
      }
    } else {
      // Position changed, reset stability timer
      stableSince = null;
    }

    lastTop = currentTop;

    if (Date.now() - startTime > MAX_WAIT_MS) {
      // Timeout — scroll anyway as a fallback
      cleanup();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add(styles.highlightPulse);
      setTimeout(() => {
        el.classList.remove(styles.highlightPulse);
      }, ANIMATION_DURATION_MS);
    }
  }, POLL_INTERVAL_MS);
}

/**
 * Queue a highlight target to be processed after the next navigation.
 * Call this BEFORE `router.push`. Starts polling immediately so it
 * catches the element as soon as it appears in the DOM and its layout
 * has stabilized, regardless of React render timing.
 */
export function queueHighlight(elementId: string) {
  startPolling(elementId);
}

/**
 * No-op hook kept for API compatibility — the actual work is done
 * by `queueHighlight` + module-level polling, which is independent
 * of React's lifecycle.
 */
export function useScrollToHighlight() {
  // Intentionally empty — polling is managed outside React
}
